import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { messagesAPI, workersAPI } from '../api/api';
import { BreadcrumbsBox } from '../components/Breadcrumbs';
import { useWebSocketContext, useWsSubscribe } from '../contexts/WebSocketContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import TranslateButton from '../components/TranslateButton';
import { haptic } from '../utils/haptics';
import { 
  ChatCircle, 
  PaperPlaneTilt, 
  MagnifyingGlass, 
  ArrowLeft,
  User,
  Clock,
  Checks,
  Check,
  Plus,
  X,
  WifiHigh,
  WifiSlash,
  Translate,
  Microphone,
  MicrophoneSlash,
  SpinnerGap,
  Play,
  Pause,
  SpeakerHigh,
  SpeakerLow,
  SpeakerNone,
  CheckCircle,
  CircleNotch,
  CalendarPlus,
  DownloadSimple
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

// Audio Player component for voice messages
const AudioMessagePlayer = ({ audioUrl, duration, isOwnMessage, messageId, played, onPlay }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [hasPlayed, setHasPlayed] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setAudioDuration(audio.duration);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handlePlay = () => {
      // Mark as played when audio starts playing (only for received messages)
      if (!isOwnMessage && !hasPlayed && onPlay) {
        setHasPlayed(true);
        onPlay(messageId);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
    };
  }, [isOwnMessage, hasPlayed, messageId, onPlay]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 min-w-[200px]">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <button
        onClick={togglePlay}
        className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          isOwnMessage 
            ? 'bg-white/20 hover:bg-white/30 border-white/30' 
            : 'bg-[#2B4C6F] hover:bg-[#4A7DA6] border-black dark:border-neutral-600 text-white'
        }`}
        data-testid="audio-play-btn"
      >
        {isPlaying ? (
          <Pause size={16} weight="bold" />
        ) : (
          <Play size={16} weight="bold" className="ml-0.5" />
        )}
      </button>
      
      <div className="flex-1 min-w-0">
        {/* Waveform placeholder / Progress bar */}
        <div className="relative h-8 flex items-center gap-0.5">
          {/* Simulated waveform bars */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`w-1 rounded-full transition-all ${
                (i / 20) * 100 <= progress
                  ? isOwnMessage ? 'bg-white' : 'bg-[#2B4C6F]'
                  : isOwnMessage ? 'bg-white/30' : 'bg-neutral-300 dark:bg-neutral-500'
              }`}
              style={{ 
                height: `${Math.random() * 60 + 40}%`,
              }}
            />
          ))}
        </div>
        
        {/* Time display */}
        <div className={`flex justify-between text-xs mt-1 font-bold ${
          isOwnMessage ? 'text-blue-200' : 'text-neutral-500 dark:text-neutral-400'
        }`}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(audioDuration)}</span>
        </div>
      </div>
      
      <SpeakerHigh className={`h-4 w-4 flex-shrink-0 ${
        isOwnMessage ? 'text-blue-200' : 'text-neutral-400 dark:text-neutral-500'
      }`} />
    </div>
  );
};

// Message bubble component with translation support
const MessageBubble = ({ message, isOwnMessage, onAudioPlay }) => {
  const [translation, setTranslation] = useState(null);
  const [bookingState, setBookingState] = useState(message.booking_accepted ? 'accepted' : 'idle');
  const isAudioMessage = message.message_type === 'audio' && message.audio_url;
  const isBookingRequest = Array.isArray(message.booking_dates) && message.booking_dates.length > 0;

  const handleAcceptBooking = async () => {
    setBookingState('loading');
    try {
      const res = await messagesAPI.acceptBooking(message._id);
      setBookingState('accepted');
      const n = res.data?.blocked_dates?.length || 0;
      toast({
        title: 'Dates blocked ✓',
        description: n ? `${n} day${n > 1 ? 's' : ''} marked as booked on your calendar. A confirmation was sent.` : 'Already accepted.',
      });
    } catch (err) {
      setBookingState('idle');
      const detail = err.response?.data?.detail;
      toast({ title: 'Could not accept booking', description: typeof detail === 'string' ? detail : 'Please try again.', variant: 'destructive' });
    }
  };

  const downloadIcs = async () => {
    try {
      const res = await messagesAPI.bookingIcs(message._id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `constructhelp-booking.ics`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Download failed', description: 'Could not generate the calendar file.', variant: 'destructive' });
    }
  };

  const googleCalUrl = () => {
    const ds = [...message.booking_dates].sort();
    const start = ds[0].replace(/-/g, '');
    const endD = new Date(ds[ds.length - 1]);
    endD.setDate(endD.getDate() + 1);
    const end = endD.toISOString().slice(0, 10).replace(/-/g, '');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('ConstructHelp job booking')}&dates=${start}/${end}&details=${encodeURIComponent('Booked dates: ' + ds.join(', ') + ' — via ConstructHelp')}`;
  };

  return (
    <div
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
      data-testid={`message-${message._id}`}
    >
      <div
        className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
          isOwnMessage
            ? 'bg-[#0A84FF] text-white rounded-br-md shadow-sm'
            : 'bg-[#E9E9EB] dark:bg-[#3A3A3C] text-neutral-900 dark:text-neutral-50 rounded-bl-md shadow-sm'
        }`}
      >
        {/* Audio message player */}
        {isAudioMessage ? (
          <AudioMessagePlayer 
            audioUrl={message.audio_url} 
            duration={message.audio_duration}
            isOwnMessage={isOwnMessage}
            messageId={message._id}
            played={message.played}
            onPlay={onAudioPlay}
          />
        ) : (
          <>
            {/* Original text message */}
            <p className="break-words">{message.content}</p>
            
            {/* Translation */}
            {translation && (
              <div className={`mt-2 pt-2 border-t ${
                isOwnMessage ? 'border-white/30' : 'border-neutral-300 dark:border-neutral-600'
              }`}>
                <p className={`text-xs mb-1 flex items-center gap-1 font-bold ${
                  isOwnMessage ? 'text-blue-200' : 'text-neutral-500 dark:text-neutral-400'
                }`}>
                  <Translate size={12} weight="bold" />
                  Translation:
                </p>
                <p className={`break-words italic ${
                  isOwnMessage ? 'text-blue-100' : 'text-neutral-700 dark:text-neutral-300'
                }`}>
                  {translation}
                </p>
              </div>
            )}
          </>
        )}

        {/* Booking request actions */}
        {isBookingRequest && (bookingState === 'accepted' || !isOwnMessage) && (
          <div className="mt-3 pt-3 border-t border-neutral-300 dark:border-neutral-600">
            {bookingState === 'accepted' ? (
              <div className="space-y-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-bold rounded-lg border border-emerald-300 dark:border-emerald-700" data-testid={`booking-accepted-${message._id}`}>
                  <CheckCircle size={14} weight="fill" /> {isOwnMessage ? 'Booking confirmed' : 'Dates blocked on your calendar'}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={downloadIcs}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-white text-neutral-900 text-xs font-bold rounded-lg border border-neutral-400 hover:bg-neutral-100 transition-colors"
                    data-testid={`booking-ics-btn-${message._id}`}
                  >
                    <DownloadSimple size={14} weight="bold" /> .ics file
                  </button>
                  <a
                    href={googleCalUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-white text-neutral-900 text-xs font-bold rounded-lg border border-neutral-400 hover:bg-neutral-100 transition-colors"
                    data-testid={`booking-gcal-link-${message._id}`}
                  >
                    <CalendarPlus size={14} weight="bold" /> Google Calendar
                  </a>
                </div>
              </div>
            ) : (
              <button
                onClick={handleAcceptBooking}
                disabled={bookingState === 'loading'}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50"
                data-testid={`accept-booking-btn-${message._id}`}
              >
                {bookingState === 'loading' ? (
                  <><CircleNotch size={16} weight="bold" className="animate-spin" /> Blocking dates...</>
                ) : (
                  <><CheckCircle size={16} weight="bold" /> Accept & block these dates</>
                )}
              </button>
            )}
          </div>
        )}
        
        {/* Footer with translate button, time, and status indicators */}
        <div className={`flex items-center justify-between mt-2 ${
          isOwnMessage ? 'text-blue-200' : 'text-neutral-400 dark:text-neutral-500'
        }`}>
          {!isAudioMessage && (
            <TranslateButton 
              text={message.content} 
              onTranslate={setTranslation}
            />
          )}
          {/* Voice message label with played indicator */}
          {isAudioMessage && (
            <span className="text-xs flex items-center gap-1 font-bold">
              <Microphone size={12} weight="bold" /> 
              Voice
              {isOwnMessage && message.played && (
                <span className="ml-1 flex items-center gap-0.5 text-emerald-300" data-testid="audio-played-indicator">
                  <SpeakerHigh size={12} weight="bold" />
                  <span>Played</span>
                </span>
              )}
            </span>
          )}
          <div className="flex items-center space-x-1">
            <span className="text-xs font-bold">
              {new Date(message.created_at).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
            {isOwnMessage && (
              isAudioMessage ? (
                // For audio messages, show played status
                message.played ? (
                  <Checks size={12} weight="bold" className="text-emerald-300" data-testid="audio-played-check" />
                ) : (
                  <Check size={12} weight="bold" />
                )
              ) : (
                // For text messages, show read status
                message.read ? (
                  <Checks size={12} weight="bold" />
                ) : (
                  <Check size={12} weight="bold" />
                )
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPartnerId = searchParams.get('partner');
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const selectedConversationRef = useRef(null);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);
  const [pendingAudioBlob, setPendingAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // Keep ref in sync with state for WebSocket callback
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      const message = data.message;
      
      // Add message to current conversation if it's the active one
      const currentConv = selectedConversationRef.current;
      if (currentConv && 
          (message.sender_id === currentConv.partner._id || 
           message.receiver_id === currentConv.partner._id)) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m._id === message._id)) return prev;
          return [...prev, message];
        });
      }
      
      // Update conversation list
      setConversations(prev => {
        const partnerId = message.sender_id === currentUserId 
          ? message.receiver_id 
          : message.sender_id;
        
        const existingIndex = prev.findIndex(c => c.partner._id === partnerId);
        
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            last_message: message,
            unread_count: message.sender_id !== currentUserId && 
                         currentConv?.partner._id !== partnerId
              ? (updated[existingIndex].unread_count || 0) + 1
              : updated[existingIndex].unread_count
          };
          // Sort by last message time
          return updated.sort((a, b) => {
            const timeA = a.last_message?.created_at ? new Date(a.last_message.created_at) : new Date(0);
            const timeB = b.last_message?.created_at ? new Date(b.last_message.created_at) : new Date(0);
            return timeB - timeA;
          });
        }
        return prev;
      });
      
      // Show notification if message is from another user and not in current conversation
      const currentConvCheck = selectedConversationRef.current;
      if (message.sender_id !== currentUserId && 
          (!currentConvCheck || currentConvCheck.partner._id !== message.sender_id)) {
        toast({
          title: `New message from ${message.sender_name || 'User'}`,
          description: message.content.substring(0, 50) + (message.content.length > 50 ? '...' : ''),
        });
      }
    } else if (data.type === 'typing') {
      // Show typing indicator
      const currentConv = selectedConversationRef.current;
      if (currentConv && data.user_id === currentConv.partner._id) {
        setPartnerTyping(data.is_typing);
      }
    } else if (data.type === 'messages_read') {
      // Update read status for messages
      if (selectedConversationRef.current?.partner._id === data.reader_id) {
        setMessages(prev => prev.map(m => 
          m.sender_id === currentUserId ? { ...m, read: true } : m
        ));
      }
    } else if (data.type === 'audio_played') {
      // Update played status for audio messages (voice message read receipt)
      setMessages(prev => prev.map(m => 
        m._id === data.message_id 
          ? { ...m, played: true, played_at: data.played_at } 
          : m
      ));
      
      // Show subtle notification that voice message was played
      const currentConv = selectedConversationRef.current;
      if (currentConv && data.played_by === currentConv.partner._id) {
        toast({
          title: 'Voice message played',
          description: `${currentConv.partner.name || 'User'} listened to your voice message`,
        });
      }
    }
  }, [currentUserId]);

  useWsSubscribe(handleWebSocketMessage);
  const { isConnected, sendTypingIndicator, sendReadReceipt } = useWebSocketContext();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Get current user ID from token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUserId(payload.sub);
    } catch (e) {
      console.error('Error parsing token:', e);
    }
    
    fetchConversations();
  }, [navigate]);

  // Track if we've handled the initial partner
  const initialPartnerHandledRef = useRef(false);

  useEffect(() => {
    // Reset the handled flag when partnerId changes
    initialPartnerHandledRef.current = false;
  }, [initialPartnerId]);

  useEffect(() => {
    if (initialPartnerId && !loading && !initialPartnerHandledRef.current) {
      const conv = conversations.find(c => c.partner._id === initialPartnerId);
      if (conv) {
        initialPartnerHandledRef.current = true;
        handleSelectConversation(conv);
      } else if (conversations.length >= 0) {
        initialPartnerHandledRef.current = true;
        // No existing conversation - fetch user info and start new chat
        const fetchPartnerAndStartChat = async () => {
          try {
            // Try to get user info (could be worker or contractor)
            const response = await messagesAPI.searchUsers(initialPartnerId);
            const users = response.data.users || [];
            
            // Find the exact user by ID
            let partner = users.find(u => u._id === initialPartnerId);
            
            // If not found in search, try fetching directly
            if (!partner) {
              try {
                const workerRes = await workersAPI.getById(initialPartnerId);
                if (workerRes.data.worker) {
                  partner = workerRes.data.worker;
                }
              } catch (e) {
                console.log('Not a worker, trying other methods...');
              }
            }
            
            if (partner) {
              const newConv = {
                partner: partner,
                last_message: null,
                unread_count: 0
              };
              setConversations(prev => {
                // Check if already exists
                if (prev.some(c => c.partner._id === partner._id)) {
                  return prev;
                }
                return [newConv, ...prev];
              });
              setSelectedConversation(newConv);
              setMessages([]);
            }
          } catch (error) {
            console.error('Error fetching partner for new chat:', error);
          }
        };
        fetchPartnerAndStartChat();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPartnerId, conversations, loading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messagesAPI.getConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load conversations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setPartnerTyping(false);
    try {
      const response = await messagesAPI.getMessages(conversation.partner._id);
      setMessages(response.data.messages || []);
      
      // Send read receipt via WebSocket
      if (isConnected) {
        sendReadReceipt(conversation.partner._id);
      }
      
      // Update unread count in conversation list
      setConversations(prev => 
        prev.map(c => 
          c.partner._id === conversation.partner._id 
            ? { ...c, unread_count: 0 }
            : c
        )
      );
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive',
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    
    // Stop typing indicator
    if (isConnected) {
      sendTypingIndicator(selectedConversation.partner._id, false);
    }
    
    try {
      await messagesAPI.sendMessage({
        receiver_id: selectedConversation.partner._id,
        content: newMessage.trim()
      });
      haptic(12);
      
      // Clear input - WebSocket will handle adding the message
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle typing indicator
  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    if (selectedConversation && isConnected) {
      // Send typing indicator
      sendTypingIndicator(selectedConversation.partner._id, value.length > 0);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of no input
      if (value.length > 0) {
        typingTimeoutRef.current = setTimeout(() => {
          sendTypingIndicator(selectedConversation.partner._id, false);
        }, 2000);
      }
    }
  };

  const handleSearchUsers = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await messagesAPI.searchUsers(query);
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm format which is widely supported
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      setRecordingDuration(0);
      
      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        // Clear timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
        
        // Store blob and show options
        setPendingAudioBlob(audioBlob);
        setShowVoiceOptions(true);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      toast({
        title: 'Recording started',
        description: 'Speak now... Click the mic button again to stop.',
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: 'Microphone access denied',
        description: 'Please allow microphone access to use voice features.',
        variant: 'destructive',
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    setShowVoiceOptions(false);
    setPendingAudioBlob(null);
    setRecordingDuration(0);
  };

  const sendAsVoiceMessage = async () => {
    if (!pendingAudioBlob || !selectedConversation) return;
    
    setIsTranscribing(true);
    try {
      await messagesAPI.sendAudioMessage(
        pendingAudioBlob,
        selectedConversation.partner._id,
        recordingDuration
      );
      
      toast({
        title: 'Voice message sent',
        description: 'Your voice message has been delivered.',
      });
      
      setShowVoiceOptions(false);
      setPendingAudioBlob(null);
      setRecordingDuration(0);
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast({
        title: 'Failed to send',
        description: error.response?.data?.detail || 'Failed to send voice message.',
        variant: 'destructive',
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const convertToText = async () => {
    if (!pendingAudioBlob) return;
    
    setIsTranscribing(true);
    try {
      const response = await messagesAPI.transcribeAudio(pendingAudioBlob);
      
      if (response.data.success && response.data.text) {
        setNewMessage(prev => {
          const separator = prev.trim() ? ' ' : '';
          return prev + separator + response.data.text;
        });
        
        toast({
          title: 'Transcription complete',
          description: 'Your voice has been converted to text.',
        });
      }
      
      setShowVoiceOptions(false);
      setPendingAudioBlob(null);
      setRecordingDuration(0);
    } catch (error) {
      console.error('Transcription error:', error);
      toast({
        title: 'Transcription failed',
        description: error.response?.data?.detail || 'Failed to convert voice to text.',
        variant: 'destructive',
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Handle marking audio as played (read receipt for voice messages)
  const handleAudioPlay = async (messageId) => {
    try {
      await messagesAPI.markAudioPlayed(messageId);
      // Update local message state to reflect played status
      setMessages(prev => prev.map(msg => 
        msg._id === messageId 
          ? { ...msg, played: true, played_at: new Date().toISOString() }
          : msg
      ));
    } catch (error) {
      console.error('Error marking audio as played:', error);
      // Don't show error toast - this is a background operation
    }
  };

  const handleStartNewChat = (user) => {
    // Check if conversation already exists
    const existingConv = conversations.find(c => c.partner._id === user._id);
    if (existingConv) {
      handleSelectConversation(existingConv);
    } else {
      // Create new conversation entry
      const newConv = {
        partner: user,
        last_message: null,
        unread_count: 0
      };
      setConversations(prev => [newConv, ...prev]);
      setSelectedConversation(newConv);
      setMessages([]);
    }
    setShowNewChat(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="ios-dash min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="messages-page">
      {/* Mobile Header */}
      <div className="bg-[#2B4C6F] text-white py-4 border-b-4 border-black dark:border-[#2B4C6F] md:hidden">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-2xl font-black">Messages</h1>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto md:px-4 md:py-6">
        {/* Desktop Breadcrumbs */}
        <BreadcrumbsBox 
          customItems={[
            { label: 'Home', path: localStorage.getItem('userType') === 'worker' ? '/worker-dashboard' : '/contractor-dashboard', isHome: true },
            { label: 'Messages', path: '/messages', isCurrent: true }
          ]}
          className="mb-4 hidden md:block"
        />
        
        <div className="bg-white dark:bg-neutral-800 md:border-2 md:border-black dark:md:border-neutral-700 md:rounded-xl md:shadow-[4px_4px_0px_#000] dark:md:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden" style={{ height: 'calc(100vh - 160px)', minHeight: '500px' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className={`border-r-2 border-neutral-200 dark:border-neutral-700 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {/* Header */}
              <div className="p-4 border-b-2 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-black text-neutral-900 dark:text-white">Messages</h2>
                  <button
                    onClick={() => setShowNewChat(true)}
                    className="w-10 h-10 flex items-center justify-center bg-[#2B4C6F] text-white rounded-lg border-2 border-black"
                    data-testid="new-chat-btn"
                  >
                    <Plus size={20} weight="bold" />
                  </button>
                </div>
              </div>

              {/* New Chat Search */}
              {showNewChat && (
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">New Conversation</span>
                    <button onClick={() => { setShowNewChat(false); setSearchQuery(''); setSearchResults([]); }}>
                      <X size={16} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                    </button>
                  </div>
                  <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <Input
                      type="text"
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => handleSearchUsers(e.target.value)}
                      className="pl-10 dark:bg-neutral-800 dark:border-neutral-600 dark:text-white"
                      data-testid="search-users-input"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user._id}
                          onClick={() => handleStartNewChat(user)}
                          className="w-full p-2 flex items-center space-x-3 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                          data-testid={`search-result-${user._id}`}
                        >
                          <div className="w-10 h-10 bg-[#2B4C6F]/20 dark:bg-[#2B4C6F]/30 rounded-full flex items-center justify-center">
                            <User size={20} weight="bold" className="text-[#2B4C6F]" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-neutral-900 dark:text-white">{user.name}</p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">{user.user_type} • {user.location}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Conversations */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B4C6F]"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <ChatCircle size={48} weight="light" className="text-neutral-300 dark:text-neutral-600 mb-4" />
                    <p className="text-neutral-500 dark:text-neutral-400">No conversations yet</p>
                    <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">Start a new chat to connect</p>
                    <Button
                      className="mt-4 bg-[#2B4C6F] hover:bg-[#4A7DA6]"
                      onClick={() => setShowNewChat(true)}
                    >
                      Start New Chat
                    </Button>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.partner._id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`w-full p-4 flex items-start space-x-3 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors border-b border-neutral-100 dark:border-neutral-700 ${
                        selectedConversation?.partner._id === conv.partner._id ? 'bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20' : ''
                      }`}
                      data-testid={`conversation-${conv.partner._id}`}
                    >
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#2B4C6F] to-[#4A7DA6] rounded-full flex items-center justify-center text-white font-semibold">
                          {conv.partner.name?.charAt(0).toUpperCase()}
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-neutral-900 dark:text-white truncate">{conv.partner.name}</p>
                          {conv.last_message && (
                            <span className="text-xs text-neutral-500 dark:text-neutral-400">
                              {formatTime(conv.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                          {conv.last_message?.content || 'No messages yet'}
                        </p>
                        <Badge variant="secondary" className="mt-1 text-xs dark:bg-neutral-700 dark:text-neutral-300">
                          {conv.partner.user_type}
                        </Badge>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`col-span-2 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center space-x-4">
                    <button
                      className="md:hidden"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft size={20} weight="bold" className="text-neutral-600 dark:text-neutral-400" />
                    </button>
                    <div className="w-10 h-10 bg-gradient-to-br from-[#2B4C6F] to-[#4A7DA6] rounded-full flex items-center justify-center text-white font-semibold">
                      {selectedConversation.partner.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-neutral-900 dark:text-white">{selectedConversation.partner.name}</h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {partnerTyping ? (
                          <span className="text-[#2B4C6F] animate-pulse">typing...</span>
                        ) : (
                          `${selectedConversation.partner.user_type} • ${selectedConversation.partner.location}`
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isConnected ? (
                        <span className="flex items-center text-xs text-emerald-600 dark:text-emerald-400">
                          <WifiHigh size={12} weight="bold" className="mr-1" />
                          Live
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-neutral-400">
                          <WifiSlash size={12} weight="bold" className="mr-1" />
                          Offline
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <ChatCircle size={48} weight="bold" className="text-neutral-300 dark:text-neutral-600 mb-4" />
                        <p className="text-neutral-500 dark:text-neutral-400 font-bold">No messages yet</p>
                        <p className="text-sm text-neutral-400 dark:text-neutral-500">Send a message to start the conversation</p>
                      </div>
                    ) : (
                      <>
                        {/* Translation info banner */}
                        <div className="flex items-center justify-center gap-2 py-3 px-4 bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20 rounded-lg border-2 border-[#2B4C6F]/30 dark:border-[#2B4C6F]/40 text-sm text-[#2B4C6F] dark:text-[#7AAED4] font-bold">
                          <Translate size={16} weight="bold" />
                          <span>Click the translate icon on any message — 12 languages, translated to your preferred language</span>
                        </div>
                        
                        {messages.map((msg) => (
                          <MessageBubble 
                            key={msg._id}
                            message={msg}
                            isOwnMessage={msg.sender_id === currentUserId}
                            onAudioPlay={handleAudioPlay}
                          />
                        ))}
                      </>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Voice Options Panel - shown after recording */}
                  {showVoiceOptions && (
                    <div className="p-4 border-t-2 border-neutral-200 dark:border-neutral-700 bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#2B4C6F]/20 dark:bg-[#2B4C6F]/30 rounded-lg border-2 border-black dark:border-neutral-600 flex items-center justify-center">
                            <Microphone size={20} weight="bold" className="text-[#2B4C6F]" />
                          </div>
                          <div>
                            <p className="font-bold text-neutral-900 dark:text-white">Voice Recording</p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                              {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')} recorded
                            </p>
                          </div>
                        </div>
                        <button 
                          onClick={cancelRecording}
                          className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300"
                          data-testid="cancel-recording-btn"
                        >
                          <X size={20} weight="bold" />
                        </button>
                      </div>
                      
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">What would you like to do with this recording?</p>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={sendAsVoiceMessage}
                          disabled={isTranscribing}
                          className="flex-1 h-12 bg-[#2B4C6F] hover:bg-[#4A7DA6] border-2 border-black dark:border-neutral-600 font-bold"
                          data-testid="send-voice-message-btn"
                        >
                          {isTranscribing ? (
                            <SpinnerGap size={16} weight="bold" className="animate-spin mr-2" />
                          ) : (
                            <SpeakerHigh size={16} weight="bold" className="mr-2" />
                          )}
                          Send Voice
                        </Button>
                        <Button
                          onClick={convertToText}
                          disabled={isTranscribing}
                          variant="outline"
                          className="flex-1 h-12 border-2 border-black dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white font-bold"
                          data-testid="convert-to-text-btn"
                        >
                          {isTranscribing ? (
                            <SpinnerGap size={16} weight="bold" className="animate-spin mr-2" />
                          ) : (
                            <ChatCircle size={16} weight="bold" className="mr-2" />
                          )}
                          Convert to Text
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Message Input */}
                  {!showVoiceOptions && (
                    <form onSubmit={handleSendMessage} className="p-4 border-t-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
                      <div className="flex space-x-2">
                        <Input
                          type="text"
                          placeholder={isRecording ? "Recording... Click mic to stop" : isTranscribing ? "Processing..." : "iMessage-style… type or speak"}
                          value={newMessage}
                          onChange={handleMessageInputChange}
                          className="flex-1 h-11 border border-neutral-300 dark:border-neutral-600 bg-[#F2F2F7] dark:bg-neutral-700 dark:text-white rounded-full px-4"
                          disabled={isRecording || isTranscribing}
                          data-testid="message-input"
                        />
                        <Button
                          type="button"
                          onClick={handleMicClick}
                          disabled={isTranscribing}
                          className={`h-11 w-11 rounded-full border-0 ${
                            isRecording 
                              ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                              : 'bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300'
                          }`}
                          data-testid="voice-record-btn"
                        >
                          {isTranscribing ? (
                            <SpinnerGap size={18} weight="bold" className="animate-spin" />
                          ) : isRecording ? (
                            <MicrophoneSlash size={18} weight="bold" />
                          ) : (
                            <Microphone size={18} weight="bold" />
                          )}
                        </Button>
                        <Button
                          type="submit"
                          disabled={!newMessage.trim() || sendingMessage || isRecording || isTranscribing}
                          className="h-11 w-11 rounded-full bg-[#0A84FF] hover:bg-[#0071E3] border-0"
                          data-testid="send-message-btn"
                        >
                          <PaperPlaneTilt size={18} weight="fill" />
                        </Button>
                      </div>
                      {isRecording && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-2 flex items-center gap-1 font-bold">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                          Recording: {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')} — Click mic to stop
                        </p>
                      )}
                    </form>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-neutral-50 dark:bg-neutral-900">
                  <div className="w-20 h-20 bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/20 rounded-xl border-2 border-black dark:border-neutral-700 flex items-center justify-center mb-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
                    <ChatCircle size={40} weight="bold" className="text-[#2B4C6F]" />
                  </div>
                  <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2">Your Messages</h3>
                  <p className="text-neutral-500 dark:text-neutral-400 max-w-sm">
                    Select a conversation from the list or start a new chat to connect with contractors and workers.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
